/**
 * BILLING AND ACCOUNTS — WHAT IS OWED, WHAT IS NOT YET BILLED, AND WHAT CAME IN.
 *
 * WHAT THIS RAISES IS NOT A TAX INVOICE, and the screen says so where a reader
 * would otherwise assume. CTS raises a PROFORMA and a DELIVERY MEMO; the
 * boundary between CTS and Tally, and the CA-approved tax matrix, are unruled
 * (D263). Calling a proforma an invoice on a screen is how it becomes one in
 * somebody's filing, so it is never called one here.
 *
 * THREE FIGURES ARE KEPT APART because they answer different questions and
 * conflating them is the oldest mistake in this business (D161):
 *   PAYABLE   — billed, minus paid, plus corrections. Money he owes today.
 *   UNBILLED  — earned and not yet billed. Money he will owe.
 *   HOLDING   — the replacement value of cylinders he has. Not money at all,
 *               until one of them fails to come back.
 *
 * And the balance is RECOMPUTED beside the stored one. When they disagree the
 * screen says so rather than picking the prettier number (D127).
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Loading, Money, Pill, Register, Select, Text, type Column, Seg, EmptyState } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, days, money, rupeesToPaise, today, words, instant } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('billing')!.messages;

/** A bill's kind, in the office's words. */
function billKind(kind: string): string {
  return kind === 'event' ? 'Damage / charge' : kind === 'monthly' ? 'Monthly holding' : kind === 'delivery_memo' ? 'Delivery memo' : words(kind);
}

export function Billing({ id, session }: { id: string | null; session: Session }) {
  // CTS1's Billing was tabs, not one long page (Register · Overview · Aging ·
  // Invoices · Receipts). CTS2 has two of those faces today and says so:
  // ageing is the accounting home, the account is where money is taken.
  const isCustomer = !!id && id !== 'aging' && !Number.isNaN(Number(id));
  const [tab, setTab] = useState<'aging' | 'account'>(isCustomer ? 'account' : 'aging');
  const [customerId, setCustomerId] = useState(isCustomer ? id! : '');
  // the picker hands back the row it chose, so the name needs no second fetch
  const [customerName, setCustomerName] = useState('');
  // the route is the truth: #/billing/5 -> #/billing must show the worklist
  // again, and the first version kept the stale picker (found by the walk)
  useEffect(() => { setCustomerId(isCustomer ? id! : ''); setTab(isCustomer ? 'account' : 'aging'); }, [id]); // eslint-disable-line

  return (
    <Page title="Billing" sub="Who owes and for how long, and one customer's account — what they owe, what they will owe, what they are holding.">
      <p className="muted" style={{ marginTop: 0 }}>These are proformas and delivery memos, not tax invoices.</p>
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <Seg name="billing-tab" value={tab}
             onChange={(v) => { if (v === 'aging') { setTab('aging'); setCustomerId(''); go('#/billing'); } else { setTab('account'); } }}
             options={[{ value: 'aging', label: 'Ageing — who owes' }, { value: 'account', label: 'Customer account' }]} />
        <CustomerPickerById name="customer" label="Customer" customerId={customerId}
                            onPick={(v, c) => { setCustomerId(v); setCustomerName(c?.name ?? ''); go(v ? `#/billing/${v}` : '#/billing'); }} placeholder="Choose a customer" />
      </div>
      {tab === 'account' && customerId
        ? <Account customerId={Number(customerId)} session={session}
                   customerName={customerName} />
        : tab === 'account'
        ? <Card><EmptyState title="Whose account?" detail="Choose a customer above — or read the ageing tab and click the row that worries you." /></Card>
        : <><AgingWorklist session={session} />{can(session, 'view.revenue') ? <RevenueByKind /> : null}</>}
    </Page>
  );
}

/** 10 Oct — THE CUSTOMER'S MONEY, in one panel: used by the Money screen and carried by the customer hub as its Money tab. */
export function Account({ customerId, session, customerName }: { customerId: number; session: Session; customerName: string }) {
  const asked = useAsk(() => api.view('account.balance', { customerId }), [customerId], MESSAGES);
  const overdue = useAsk(() => api.view('cylinders.overdue', { customerId, pageSize: 100 }), [customerId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [paying, setPaying] = useState(false);
  // The allocation card asks its own two questions, so reloading the ACCOUNT does not
  // refresh it. A receipt taken on this screen must appear there immediately — a clerk
  // records the money and allocates it in one sitting, and a card still showing the
  // state before the receipt is a card that is simply wrong.
  const [accountChanged, setAccountChanged] = useState(0);
  const bumpAccount = (): void => setAccountChanged((n) => n + 1);
  const [reversing, setReversing] = useState<Record<number, string>>({});
  const [refunding, setRefunding] = useState<Record<number, string>>({});
  const [noteAmount, setNoteAmount] = useState('');
  const [noteReason, setNoteReason] = useState('');

  async function reversePay(paymentId: number) {
    const reason = (reversing[paymentId] ?? '').trim();
    if (!reason) return;
    const ok = await runner.run((retryKey) => api.command('payment.reverse', { paymentId, reason }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReversing({}); asked.reload(); }
  }
  async function creditNote() {
    const ok = await runner.run((retryKey) => api.command('creditnote.issue',
      { customerId, amountPaise: Math.round(Number(noteAmount) * 100), reason: noteReason.trim() },
      retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setNoteAmount(''); setNoteReason(''); asked.reload(); }
  }

  // only the FIRST load blanks the screen: a reload after a payment or a
  // prepare keeps the cards mounted, or every child's state (the draft under
  // review, most painfully) is thrown away mid-walk (found by the browser walk)
  if (asked.loading && !asked.data) return <Card><Loading what="the account" /></Card>;
  if (asked.error) return <Card><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const b = asked.data!;

  async function refundSd(paymentId: number) {
    const ok = await runner.run((retryKey) => api.command('sd.refund', { paymentId, reason: (refunding[paymentId] ?? '').trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setRefunding((old) => ({ ...old, [paymentId]: '' })); asked.reload(); }
  }

  async function sendBill(billId: number) {
    const ok = await runner.run((retryKey) => api.command('bill.send', { billId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) asked.reload();
  }

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {!b.agrees ? (
        <Banner kind="error" title="The stored balance and the recomputed one do not agree."
                detail={`On file ${money(b.onFile?.payablePaise ?? null)}, recounted ${money(b.payablePaise)}. The figures below are the recounted ones — tell a manager.`} />
      ) : null}

      <div className="toolbar" style={{ alignItems: 'center', marginBottom: 12 }}>
        {customerName ? <h3 style={{ margin: 0 }}>{customerName}</h3> : null}
        <Button small onClick={() => go(`#/customers/${customerId}?tab=bills`)}>Open the statement</Button>
      </div>

      <div className="kpis">
        {b.sd.heldPaise > 0 || b.sd.receipts.length > 0 ? (
          <a className="kpi" href={b.sd.receipts[0]?.documentId ? api.documentUrl(b.sd.receipts[0].documentId) : '#'} target="_blank" rel="noreferrer"
             style={{ ['--accent' as string]: 'var(--money)', textDecoration: 'none' }} title="open the SD receipt">
            <div className="kpi-label">SD held</div><div className="kpi-value">{money(b.sd.heldPaise)}</div>
            <div className="kpi-note">{b.sd.returnable ? 'nothing is owed — may be returned' : `returnable only after all dues and holding charges are paid · ${b.sd.receipts.filter((r) => !r.returned).length} receipt${b.sd.receipts.filter((r) => !r.returned).length === 1 ? '' : 's'} · click to open`}</div>
          </a>) : null}
        <div className="kpi" style={{ ['--accent' as string]: 'var(--danger)' }}>
          <div className="kpi-label">Owed today</div><div className="kpi-value">{money(b.payablePaise)}</div>
          <div className="kpi-note">billed, less paid, plus corrections{b.oldestOpenDays !== null ? ` · oldest ${days(b.oldestOpenDays)}` : ''}</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--money)' }}>
          <div className="kpi-label">Not yet billed</div><div className="kpi-value">{money(b.parts.unbilledPaise)}</div>
          <div className="kpi-note">earned and not on a bill — they will owe this</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--primary)' }}>
          <div className="kpi-label">Cylinders held</div><div className="kpi-value">{count(b.parts.cylindersOut)}</div>
          <div className="kpi-note">worth {money(b.parts.cylindersHeldPaise)} to replace — not money owed</div>
        </div>

        <div className="kpi" style={{ ['--accent' as string]: 'var(--muted)' }}>
          <div className="kpi-label">Owed plus cylinders</div><div className="kpi-value">{money(b.exposurePaise)}</div>
          <div className="kpi-note">money owed, unbilled, and the cylinders' replacement value together</div>
        </div>
      </div>

      <div className="grid two">

      {b.sd.receipts.length > 0 ? (
      <Card title="SD — security deposits" sub="the customer's money, held against our cylinders — returned only after all dues and holding charges are paid and the cylinders are home">
        {!b.sd.returnable ? <p className="muted">Not returnable yet: {b.sd.blockedBy.join('; ')}.</p> : <p className="muted">Nothing is owed — an SD may be returned.</p>}
        <Register
          columns={[
            { key: 'receipt', head: 'SD receipt', cell: (r: { receiptNumber: string | null; paymentId: number; documentId: number | null }) =>
                r.documentId ? <a href={api.documentUrl(r.documentId)} target="_blank" rel="noreferrer">{r.receiptNumber ?? `#${r.paymentId}`}</a> : (r.receiptNumber ?? `#${r.paymentId}`) },
            { key: 'on', head: 'Received', cell: (r: { paidOn: string }) => date(r.paidOn) },
            { key: 'note', head: 'For', cell: (r: { note: string | null }) => r.note ?? <span className="muted">—</span> },
            { key: 'amount', head: 'Amount', right: true, cell: (r: { amountPaise: number }) => <Money paise={r.amountPaise} /> },
            { key: 'state', head: 'Status', cell: (r: { returned: boolean }) => r.returned ? <Pill tone="muted">returned</Pill> : <Pill tone="money">held</Pill> },
            { key: 'refund', head: '', cell: (r: { paymentId: number; returned: boolean }) => can(session, 'sd.refund') && !r.returned
                ? <span style={{ display: 'flex', gap: 6 }}>
                    <Text name={`sd-why-${r.paymentId}`} label="" value={refunding[r.paymentId] ?? ''}
                          onChange={(v) => setRefunding((old) => ({ ...old, [r.paymentId]: v }))} />
                    <Button small kind="danger" disabled={runner.busy || !b.sd.returnable || !(refunding[r.paymentId] ?? '').trim()}
                            onClick={() => { void refundSd(r.paymentId); }}>Return the SD</Button>
                  </span>
                : null },
          ]}
          rows={b.sd.receipts} empty="No SD on record." />
      </Card>) : null}

        <Card title="How this adds up">
          <div className="detail-grid">
            <div><div className="k">Billed</div><div className="v"><Money paise={b.parts.billedPaise} /></div></div>
            <div><div className="k">Paid</div><div className="v"><Money paise={b.parts.paidPaise} /></div></div>
            <div><div className="k">Credits</div><div className="v"><Money paise={b.parts.creditsPaise} /></div></div>
            <div><div className="k">Corrections</div><div className="v"><Money paise={b.parts.correctionsPaise} /></div></div>
          </div>
          {!b.agrees && b.onFile ? <p className="muted" style={{ marginBottom: 0 }}>The figure on file says {money(b.onFile.payablePaise)}; the difference is the finding above.</p> : null}
        </Card>

        <PrepareMonth customerId={customerId} session={session} onChanged={asked.reload} />

        <Card title="Take a payment"
              actions={can(session, 'payment.record') ? <Button small kind="primary" onClick={() => setPaying((v) => !v)}>{paying ? 'Cancel' : 'Record one'}</Button> : null}>
          {paying ? <TakePayment customerId={customerId} onDone={() => { setPaying(false); asked.reload(); bumpAccount(); }} />
            : <p className="muted" style={{ marginBottom: 0 }}>A payment is recorded against the customer, and the receipt number it gets is the one to quote.</p>}
        </Card>
      </div>

      <Card title="Recent bills" sub="a sent bill is never re-sent under the same number">
        <Register
          columns={[
            { key: 'number', head: 'Number', cell: (x: { number: string | null; billId: number }) => x.number ?? <span className="muted">#{x.billId}</span> },
            { key: 'kind', head: 'Kind', cell: (x: { kind: string }) => billKind(x.kind) },
            { key: 'sent', head: 'Sent', cell: (x: { sentAt: string | null }) => x.sentAt ? instant(x.sentAt) : <span className="muted">not sent</span> },
            { key: 'total', head: 'Total', right: true, cell: (x: { totalPaise: number }) => <Money paise={x.totalPaise} /> },
            { key: 'status', head: 'Status', cell: (x: { status: string }) => <Pill tone={x.status === 'sent' ? 'good' : 'empty'}>{x.status}</Pill> },
            { key: 'send', head: '', cell: (x: { billId: number; status: string }) => can(session, 'bill.send') && x.status !== 'sent'
                ? <Button small kind="primary" disabled={runner.busy} onClick={() => { void sendBill(x.billId); }}>Send it</Button> : null },
          ]}
          rows={b.recentBills} empty="Nothing has been billed to this customer yet." asOf={b.asOf} />
      </Card>

      <Card title="Payments" sub="a reversal is a new line with its reason — the receipt stays as issued">
        <Register
          columns={[
            { key: 'receipt', head: 'Receipt', cell: (p: { receiptNumber: string | null; paymentId: number; documentId: number | null }) =>
                p.documentId ? <a href={api.documentUrl(p.documentId)} target="_blank" rel="noreferrer" title="open the receipt">{p.receiptNumber ?? `#${p.paymentId}`}</a>
                             : (p.receiptNumber ?? <span className="muted">#{p.paymentId}</span>) },
            { key: 'on', head: 'Received', cell: (p: { paidOn: string }) => date(p.paidOn) },
            { key: 'how', head: 'How', cell: (p: { method: string; reference: string | null; instrument: string | null; instrumentNo: string | null; purpose: string }) =>
                <>{(p.instrument ?? p.method).toUpperCase()}{p.purpose === 'security_deposit' ? <> <Pill tone="money">SD</Pill></> : p.purpose === 'advance' ? <> <Pill>advance</Pill></> : null}
                  {p.instrumentNo ? <div className="muted">{p.instrumentNo}</div> : null}
                  {p.reference ? <div className="muted">{p.reference}</div> : null}</> },
            { key: 'amount', head: 'Amount', right: true, cell: (p: { amountPaise: number }) => <Money paise={p.amountPaise} /> },
            { key: 'reverse', head: '', cell: (p: { paymentId: number; amountPaise: number }) => can(session, 'payment.reverse') && p.amountPaise > 0
                ? <span style={{ display: 'flex', gap: 6 }}>
                    <Text name={`rev-why-${p.paymentId}`} label="" value={reversing[p.paymentId] ?? ''}
                          onChange={(v) => setReversing((old) => ({ ...old, [p.paymentId]: v }))} />
                    <Button small kind="danger" disabled={runner.busy || !(reversing[p.paymentId] ?? '').trim()}
                            onClick={() => { void reversePay(p.paymentId); }}>Reverse</Button>
                  </span>
                : null },
          ]}
          rows={b.recentPayments} empty="No payment has been recorded." />
      </Card>

      <Allocation customerId={customerId} session={session} changed={accountChanged} onChanged={asked.reload} />

      <Card title="Credit note" sub="money in the customer's favour, with the reason written down">
        {can(session, 'creditnote.issue') ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Text name="note-amount" label="Amount (₹)" value={noteAmount} onChange={setNoteAmount} />
            <Text name="note-reason" label="Why the customer is owed this" value={noteReason} onChange={setNoteReason} />
            <Button small kind="primary" disabled={runner.busy || !noteReason.trim() || !noteAmount.trim()}
                    onClick={() => { void creditNote(); }}>Issue the note</Button>
          </div>
        ) : <p className="muted">Issuing a note needs the creditnote.issue function.</p>}
        <Register
          columns={[
            { key: 'on', head: 'Issued', cell: (r: { on: string }) => date(r.on) },
            { key: 'why', head: 'Why', cell: (r: { reason: string | null }) => r.reason ?? '—' },
            { key: 'amount', head: 'Amount', right: true, cell: (r: { amountPaise: number }) => <Money paise={r.amountPaise} /> },
          ]}
          rows={b.creditNotes} empty="No credit note has been issued." />
      </Card>

      <Card title="Cylinders held too long"
            sub="past the point where a replacement cost may be charged — each one is a decision, not an automatic bill">
        {overdue.loading ? <Loading what="overdue cylinders" /> : overdue.error ? <Banner kind="warn" title={overdue.error.title} />
          : <Overdue rows={overdue.data!.rows} session={session} onChanged={() => { overdue.reload(); asked.reload(); }} />}
      </Card>
    </>
  );
}

/**
 * WHICH INVOICES THIS RECEIPT PAYS (0112).
 *
 * The office's real question is never "what is the balance" — it is "the customer says
 * he paid invoice 117, did he?". Answering that needs the two directions side by side:
 * what a receipt was applied to, and what an invoice has taken. So they are one card
 * with one reload, not two screens a clerk holds in their head.
 *
 * MONEY ON ACCOUNT IS SHOWN AS MONEY, NOT AS A GAP. A receipt nobody has yet applied is
 * an advance — a real state, and the commonest one on the day the bank statement arrives.
 * Whether CTS should apply it by itself, oldest invoice first, is unruled (checklist Q4), so this
 * screen never guesses; it shows the amount and lets a person say.
 *
 * THE FORM IS DELIBERATELY NOT PRE-FILLED. Filling the oldest invoice automatically
 * would be that unruled decision made quietly, in the one place nobody would look for it.
 *
 * ALLOCATING NEEDS ITS OWN PERMISSION, and so does taking one back off. A person who may
 * see the account is not thereby a person who may decide what a payment settled.
 */
function Allocation({ customerId, session, changed, onChanged }: {
  customerId: number; session: Session; changed: number; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [lines, setLines] = useState<Record<number, string>>({});
  const [why, setWhy] = useState('');
  const [takingOff, setTakingOff] = useState<Record<number, string>>({});
  // A10 — THE ALLOCATIONS LIST IS PAGED, so this screen no longer asks the server for
  // every allocation a customer has ever had. The page is part of the read's deps, so
  // turning the page re-asks; and `changed` is still there, so applying or taking off an
  // allocation re-asks the AUTHORITATIVE figures rather than patching the rows in place.
  const [allocPage, setAllocPage] = useState(1);
  const ALLOC_PAGE_SIZE = 25;
  const runner = useRun(MESSAGES);

  // `changed` is in the deps on purpose: a payment recorded elsewhere on this screen
  // bumps it, and these two reads go again.
  const recon = useAsk(() => api.view('account.reconciliation', {
    // FLAT fields: a view's query travels as a query string, where a nested object
    // becomes the text `[object Object]` (A10).
    customerId, allocationsPage: allocPage, allocationsPageSize: ALLOC_PAGE_SIZE,
  }), [customerId, changed, allocPage], MESSAGES);
  // A10 (second pass) — THE SELECTORS ARE PAGED AND SEARCHABLE.
  //
  // This asked for every unapplied receipt and every outstanding invoice the customer had
  // ever had. Bounding `reconciliation` and leaving this alone left the SCREEN unbounded,
  // which is what review found: on a large account the page could still be handed tens of
  // thousands of rows. A clerk holding a remittance advice knows the receipt number, so
  // searching is the other half of the answer — paging through four hundred of them is not
  // a workflow.
  const [spPage, setSpPage] = useState(1);
  const [spFind, setSpFind] = useState('');
  const [blPage, setBlPage] = useState(1);
  const [blFind, setBlFind] = useState('');
  const SPARE_PAGE_SIZE = 25;
  const spare = useAsk(() => api.view('account.unallocated', {
    customerId,
    paymentsPage: spPage, paymentsPageSize: SPARE_PAGE_SIZE, ...(spFind.trim() ? { paymentsFind: spFind.trim() } : {}),
    billsPage: blPage, billsPageSize: SPARE_PAGE_SIZE, ...(blFind.trim() ? { billsFind: blFind.trim() } : {}),
  }), [customerId, changed, spPage, spFind, blPage, blFind], MESSAGES);
  const mayAllocate = can(session, 'payment.allocate');
  const mayReverse = can(session, 'payment.reverse_allocation');

  function reload() { recon.reload(); spare.reload(); onChanged(); }
  /** After applying or taking off an allocation, go back to the first page and re-read. */
  function reloadFromTheTop() { setAllocPage(1); reload(); }

  const chosen = spare.data?.payments.find((p) => String(p.paymentId) === paymentId) ?? null;
  // Rupees are what a clerk types and paise are what the command takes; a figure that is
  // not money at all becomes null here rather than a silent zero.
  const entered = Object.entries(lines)
    .map(([billId, r]) => ({ billId: Number(billId), paise: r.trim() ? rupeesToPaise(r) : null }))
    .filter((x) => x.paise !== 0);
  const bad = entered.some((x) => x.paise === null);
  const asked = entered.reduce((t, x) => t + (x.paise ?? 0), 0);
  const overspend = chosen !== null && !bad && asked > chosen.unallocatedPaise;

  async function allocate() {
    const ok = await runner.run((retryKey) => api.command('payment.allocate', {
      paymentId: Number(paymentId),
      allocations: entered.map((x) => ({ billId: x.billId, amountPaise: x.paise! })),
      ...(why.trim() ? { reason: why.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setLines({}); setWhy(''); setPaymentId(''); setOpen(false); reloadFromTheTop(); }
  }

  async function takeOff(allocationId: number) {
    const ok = await runner.run((retryKey) => api.command('payment.reverseAllocation', {
      allocationId, reason: (takingOff[allocationId] ?? '').trim(),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setTakingOff((old) => ({ ...old, [allocationId]: '' })); reload(); }
    // Not reloadFromTheTop: taking one off should leave the clerk looking at the page
    // they were on. The totals above come from the same read, so they move with it.
  }

  if (recon.loading && !recon.data) return <Card title="What each receipt paid"><Loading what="the allocations" /></Card>;
  if (recon.error) return <Card title="What each receipt paid"><Banner kind="error" title={recon.error.title} /></Card>;
  const t = recon.data!.totals;

  return (
    <Card title="What each receipt paid"
          sub="one receipt may settle several invoices, and one invoice may take several receipts"
          actions={mayAllocate && ((spare.data?.pages.payments.total ?? 0) > 0)
            ? <Button small kind="primary" onClick={() => setOpen((v) => !v)}>{open ? 'Cancel' : 'Apply a receipt'}</Button>
            : null}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      <div className="detail-grid">
        <div><div className="k">Received</div><div className="v"><Money paise={t.receivedPaise} /></div></div>
        <div><div className="k">Applied to invoices</div><div className="v"><Money paise={t.allocatedPaise} /></div></div>
        <div><div className="k">On account</div><div className="v"><Money paise={t.onAccountPaise} /></div></div>
        <div><div className="k">Still outstanding</div><div className="v"><Money paise={t.outstandingPaise} /></div></div>
      </div>
      {t.onAccountPaise > 0
        ? <Banner kind="info" title={`${money(t.onAccountPaise)} is on account and not applied to any invoice.`}
                  detail="Money received that nobody has yet said what it pays. CTS does not apply it by itself — which invoice it settles is a person's decision until that rule is ruled on." />
        : null}

      {open && mayAllocate ? (
        <div style={{ marginTop: 12 }}>
          {/* FIND IT RATHER THAN SCROLL TO IT. The picker holds one page; the search is how a
            * clerk with a remittance advice in their hand reaches the receipt it names. */}
          <Text name="alloc-find" label="Find a receipt" value={spFind}
                onChange={(v) => { setSpFind(v); setSpPage(1); setPaymentId(''); }}
                help="by receipt number; leave it empty to list the oldest unapplied first" />
          <Select name="alloc-receipt" label="Receipt" value={paymentId} onChange={(v) => { setPaymentId(v); setLines({}); }}
                  options={[{ value: '', label: 'Choose the receipt' },
                            ...(spare.data?.payments ?? []).map((p) => ({
                              value: String(p.paymentId),
                              label: `${p.receiptNumber ?? `#${p.paymentId}`} · ${date(p.paidOn)} · ${money(p.unallocatedPaise)} unapplied`,
                            }))]} />
          <SelectorPager what="receipts with money on account" page={spare.data?.pages.payments}
                         onGo={setSpPage} busy={spare.loading}
                         note={spare.data ? `${money(spare.data.totals.onAccountPaise)} on account altogether` : ''} />
          {chosen ? (
            (spare.data?.pages.bills.total ?? 0) === 0
              ? <Banner kind="info" title="Nothing is outstanding." detail="Every sent invoice for this customer is settled, so this receipt stays on account." />
              : <>
                  <Register
                    columns={[
                      { key: 'number', head: 'Invoice', cell: (x: { number: string; billId: number }) => x.number || `#${x.billId}` },
                      { key: 'kind', head: 'Kind', cell: (x: { kind: string }) => billKind(x.kind) },
                      { key: 'sent', head: 'Sent', cell: (x: { sentAt: string | null }) => x.sentAt ? date(x.sentAt) : <span className="muted">not sent</span> },
                      { key: 'outstanding', head: 'Still owes', right: true, cell: (x: { outstandingPaise: number }) => <Money paise={x.outstandingPaise} /> },
                      { key: 'put', head: 'Put on it (₹)', cell: (x: { billId: number; outstandingPaise: number }) => (
                          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Text name={`alloc-${x.billId}`} label="" value={lines[x.billId] ?? ''}
                                  onChange={(v) => setLines((old) => ({ ...old, [x.billId]: v }))} />
                            <Button small kind="ghost"
                                    onClick={() => setLines((old) => ({ ...old,
                                      [x.billId]: String(Math.min(x.outstandingPaise, chosen.unallocatedPaise - asked + (lines[x.billId]?.trim() ? rupeesToPaise(lines[x.billId]!) ?? 0 : 0)) / 100) }))}>
                              Fill
                            </Button>
                          </span>) },
                    ]}
                    rows={spare.data?.bills ?? []} empty="Nothing is outstanding." />
                  <Text name="alloc-bill-find" label="Find an invoice" value={blFind}
                        onChange={(v) => { setBlFind(v); setBlPage(1); }}
                        help="by invoice number; leave it empty to list the oldest open first" />
                  <SelectorPager what="open invoices" page={spare.data?.pages.bills}
                                 onGo={setBlPage} busy={spare.loading}
                                 note={spare.data ? `${money(spare.data.totals.outstandingPaise)} outstanding altogether` : ''} />
                  {bad ? <Banner kind="warn" title="One of those is not money." detail="Rupees, with at most two decimals." /> : null}
                  {overspend ? <Banner kind="warn" title="That is more than the receipt has left."
                                       detail={`${money(asked)} asked against ${money(chosen.unallocatedPaise)} unapplied. A receipt cannot pay more than it was worth.`} /> : null}
                  <Text name="alloc-why" label="Note (optional)" value={why} onChange={setWhy}
                        help="what the customer's remittance advice said, if it said anything" />
                  <p className="muted">{`${money(asked)} of ${money(chosen.unallocatedPaise)} allocated`}</p>
                  <Button kind="primary" disabled={runner.busy || bad || overspend || entered.length === 0}
                          onClick={() => { void allocate(); }}>
                    {runner.busy ? 'Applying…' : 'Apply it'}
                  </Button>
                </>
          ) : null}
        </div>
      ) : null}

      <Register
        columns={[
          { key: 'receipt', head: 'Receipt', cell: (a: { receiptNumber: string | null; paymentId: number }) => a.receiptNumber ?? <span className="muted">#{a.paymentId}</span> },
          { key: 'bill', head: 'Invoice', cell: (a: { billNumber: string | null; billId: number }) => a.billNumber ?? <span className="muted">#{a.billId}</span> },
          { key: 'amount', head: 'Applied', right: true, cell: (a: { amountPaise: number }) => <Money paise={a.amountPaise} /> },
          { key: 'who', head: 'By', cell: (a: { allocatedByName: string | null; allocatedAt: string }) => <>{a.allocatedByName ?? '—'}<div className="muted">{date(a.allocatedAt)}</div></> },
          { key: 'state', head: 'Standing', cell: (a: { live: boolean; reverseReason: string | null; reversedByName: string | null }) => a.live
              ? <Pill tone="good">applied</Pill>
              : <><Pill tone="danger">taken off</Pill><div className="muted">{a.reverseReason ?? ''}{a.reversedByName ? ` — ${a.reversedByName}` : ''}</div></> },
          { key: 'off', head: '', cell: (a: { allocationId: number; live: boolean }) => a.live && mayReverse
              ? <span style={{ display: 'flex', gap: 6 }}>
                  <Text name={`alloc-off-${a.allocationId}`} label="" value={takingOff[a.allocationId] ?? ''}
                        onChange={(v) => setTakingOff((old) => ({ ...old, [a.allocationId]: v }))} />
                  <Button small kind="danger" disabled={runner.busy || !(takingOff[a.allocationId] ?? '').trim()}
                          onClick={() => { void takeOff(a.allocationId); }}>Take it off</Button>
                </span>
              : null },
        ]}
        rows={recon.data!.allocations}
        empty="No receipt has been applied to an invoice yet." />

      <AllocationPager page={recon.data!.pages.allocations} onGo={setAllocPage} busy={recon.loading} />

      {!mayAllocate
        ? <p className="muted" style={{ marginBottom: 0 }}>Applying a receipt to an invoice needs the payment.allocate function. Everything above is readable without it.</p>
        : null}
    </Card>
  );
}

/**
 * A10 (second pass) — the pager under each SELECTOR, which also says how much is there in
 * total. A clerk choosing what to apply needs to know whether the page in front of them is
 * all of it; the figure comes from the server over the whole account, never from the rows.
 */
function SelectorPager({ what, page, onGo, busy, note }: {
  what: string; page: { number: number; size: number; total: number } | undefined;
  onGo: (n: number) => void; busy: boolean; note: string;
}) {
  if (!page) return null;
  const last = Math.max(1, Math.ceil(page.total / page.size));
  const from = page.total === 0 ? 0 : (page.number - 1) * page.size + 1;
  const to = Math.min(page.number * page.size, page.total);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
      {last > 1
        ? <>
            <Button small kind="ghost" disabled={busy || page.number <= 1} onClick={() => onGo(page.number - 1)}>Previous</Button>
            <span className="muted">{`${from}–${to} of ${page.total} ${what}`}</span>
            <Button small kind="ghost" disabled={busy || page.number >= last} onClick={() => onGo(page.number + 1)}>Next</Button>
          </>
        : <span className="muted">{`${page.total} ${what}`}</span>}
      {note ? <span className="muted">· {note}</span> : null}
    </div>
  );
}

/**
 * A10 — the pager for the allocations list. It says how many there are in total, which is
 * a figure the server computes over the whole account and not from the rows on screen.
 */
function AllocationPager({ page, onGo, busy }: {
  page: { number: number; size: number; total: number }; onGo: (n: number) => void; busy: boolean;
}) {
  const last = Math.max(1, Math.ceil(page.total / page.size));
  if (page.total <= page.size) return null;
  const from = (page.number - 1) * page.size + 1;
  const to = Math.min(page.number * page.size, page.total);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
      <Button small kind="ghost" disabled={busy || page.number <= 1} onClick={() => onGo(page.number - 1)}>Previous</Button>
      <span className="muted">{`${from}–${to} of ${page.total}`}</span>
      <Button small kind="ghost" disabled={busy || page.number >= last} onClick={() => onGo(page.number + 1)}>Next</Button>
    </div>
  );
}

function Overdue({ rows, session, onChanged }: {
  rows: readonly { lendingId: number; cylinderId: number; internalId: string; serialNo: string; daysOut: number; outChallan: string | null; unconfirmed: boolean }[];
  session: Session; onChanged: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [acting, setActing] = useState<number | null>(null);
  const [settling, setSettling] = useState<number | null>(null);

  async function issue(lendingId: number) {
    const ok = await runner.run((retryKey) => api.command('costbill.issue', { lendingId, onDate: today() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setActing(null); onChanged(); }
  }
  async function notice(lendingId: number, state: string) {
    const ok = await runner.run((retryKey) => api.command('notice.setState', {
      lendingId, state: state as 'approaching' | 'reached' | 'notice_issued' | 'cost_billed' | 'customer_responded' | 'returned' | 'paid' | 'disputed' | 'lost',
      onDate: today(),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setActing(null); onChanged(); }
  }

  const columns: readonly Column<{ lendingId: number; cylinderId: number; internalId: string; serialNo: string; daysOut: number; outChallan: string | null; unconfirmed: boolean }>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => <a href={`#/cylinders/${r.cylinderId}`}>{r.internalId}</a> },
    { key: 'serial', head: 'Serial', cell: (r) => r.serialNo },
    { key: 'out', head: 'Out for', right: true, cell: (r) => <strong>{days(r.daysOut)}</strong> },
    { key: 'challan', head: 'Went out on', cell: (r) => r.outChallan ?? <span className="muted">—</span> },
    { key: 'note', head: '', cell: (r) => r.unconfirmed ? <Pill tone="empty">delivery unconfirmed</Pill> : null },
    { key: 'act', head: '', cell: (r) => can(session, 'notice.set_state') || can(session, 'costbill.issue')
        ? <Button small onClick={() => setActing((v) => v === r.lendingId ? null : r.lendingId)}>{acting === r.lendingId ? 'Close' : 'Decide about it'}</Button> : null },
  ];

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Register columns={columns} rows={rows} empty="Nothing this customer holds is overdue." />
      {acting !== null ? (
        <Card title="What happens next with this cylinder"
              sub="a notice is a warning; a cost bill charges the replacement value and is not undone by the cylinder turning up later without a decision">
          <div className="toolbar">
            {can(session, 'notice.set_state') ? (
              <>
                <Button small onClick={() => { void notice(acting, 'notice_issued'); }} disabled={runner.busy}>Issue a notice</Button>
                <Button small onClick={() => { void notice(acting, 'customer_responded'); }} disabled={runner.busy}>He responded</Button>
                <Button small onClick={() => { void notice(acting, 'disputed'); }} disabled={runner.busy}>He disputes it</Button>
              </>
            ) : null}
            {can(session, 'costbill.issue') ? (
              <Button small kind="danger" onClick={() => { void issue(acting); }} disabled={runner.busy}>Bill the replacement cost</Button>
            ) : null}
            {can(session, 'eventbill.adjust') ? (
              <Button small onClick={() => setSettling((v) => v === acting ? null : acting)}>It came back</Button>
            ) : null}
          </div>
          {settling === acting ? <LateReturn lendingId={acting} onDone={() => { setSettling(null); setActing(null); onChanged(); }} /> : null}
        </Card>
      ) : null}
    </>
  );
}

/**
 * THE CYLINDER THAT CAME BACK AFTER IT WAS BILLED.
 *
 * A replacement cost already charged is not undone by the cylinder turning up;
 * somebody DECIDES what happens to the money, and that decision is recorded
 * with its reason. Cancelled in full, reduced to an agreed figure, or kept —
 * three different outcomes, and the customer will ask which one it was.
 */
function LateReturn({ lendingId, onDone }: { lendingId: number; onDone: () => void }) {
  const [outcome, setOutcome] = useState('cancelled');
  const [returnedOn, setReturnedOn] = useState(today());
  const [rupees, setRupees] = useState('');
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  const reduced = outcome === 'reduced';
  const paise = rupees.trim() ? rupeesToPaise(rupees) : null;

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('eventbill.resolveLate', {
      lendingId, outcome: outcome as 'cancelled' | 'reduced' | 'kept', returnedOn, reason: reason.trim(),
      ...(reduced && paise !== null ? { reducedToPaise: paise } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <Card title="It came back after it was billed" sub="the money does not reverse itself — say what happens to it">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Select name="late-outcome" label="What happens to the charge" value={outcome} onChange={setOutcome}
                  options={[{ value: 'cancelled', label: 'Cancelled in full' },
                            { value: 'reduced', label: 'Reduced to an agreed figure' },
                            { value: 'kept', label: 'Kept — the customer still owes it' }]} />
          <Text name="late-returned" label="Came back on" type="date" value={returnedOn} onChange={setReturnedOn} required />
          {reduced ? <Text name="late-reduced" label="Reduced to (₹)" value={rupees} onChange={setRupees} required /> : null}
        </div>
        <div>
          <Banner kind="info" title="The site the correction lands on is not chosen here."
                  detail="It is read from the trip's own history and from the bill being reversed, so a correction cannot be quietly moved to a different site." />
        </div>
      </div>
      <Text name="late-reason" label="Why" value={reason} onChange={setReason} required />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || (reduced && paise === null)}>
        {runner.busy ? 'Recording…' : 'Settle it'}
      </Button>
    </Card>
  );
}

function TakePayment({ customerId, onDone }: { customerId: number; onDone: () => void }) {
  const [rupees, setRupees] = useState(''); const [instrument, setInstrument] = useState('neft');
  const [instrumentNo, setInstrumentNo] = useState(''); const [instrumentDate, setInstrumentDate] = useState('');
  const [drawnOn, setDrawnOn] = useState(''); const [purpose, setPurpose] = useState('against_bills'); const [note, setNote] = useState('');
  const [paidOn, setPaidOn] = useState(today()); const [reference, setReference] = useState('');
  const cash = instrument === 'cash';
  const [creditDays, setCreditDays] = useState('30'); const [graceDays, setGraceDays] = useState('0'); const [interest, setInterest] = useState('1.5');
  const [receipt, setReceipt] = useState<{ number: string; documentId: number } | null>(null);
  const runner = useRun(MESSAGES);
  const paise = rupees.trim() ? rupeesToPaise(rupees) : null;

  async function submit() {
    const ok = await runner.run(async (retryKey) => {
      const r = await api.command('payment.record', {
        customerId, method: cash ? 'cash' : 'bank', amountPaise: paise!, paidOn,
        // 0170 — how the money came, and what for
        instrument: instrument as 'cash' | 'cheque' | 'dd' | 'neft' | 'rtgs' | 'imps' | 'upi',
        purpose: purpose as 'against_bills' | 'security_deposit' | 'advance',
        ...(!cash && instrumentNo.trim() ? { instrumentNo: instrumentNo.trim() } : {}),
        ...(!cash && instrumentDate ? { instrumentDate } : {}),
        ...(!cash && drawnOn.trim() ? { drawnOn: drawnOn.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        terms: { creditDays: Number(creditDays), graceDays: Number(graceDays), interestMonthlyPct: Number(interest) },
      }, retryKey ? { idempotencyKey: retryKey } : {});
      const v = r.value as { receiptNumber?: string; receiptDocumentId?: number };
      if (v.receiptNumber && v.receiptDocumentId) setReceipt({ number: v.receiptNumber, documentId: v.receiptDocumentId });
      return r;
    });
    void ok; // the receipt banner takes over on success; Done hands control back
  }

  if (receipt) {
    return (
      <>
        <Banner kind="good" title={`Receipt ${receipt.number} issued.`}
                detail="The paper was drawn the moment the money was recorded." />
        <p><a href={api.documentUrl(receipt.documentId)} target="_blank" rel="noreferrer">Open the receipt</a></p>
        <Button kind="primary" onClick={onDone}>Done</Button>
      </>
    );
  }

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {paise === null && rupees.trim() ? <Banner kind="warn" title="That is not money." detail="Rupees, with at most two decimals." /> : null}
      <Text name="pay-amount" label="Amount (₹)" value={rupees} onChange={setRupees} required autoFocus />
      <Select name="pay-method" label="How" value={instrument} onChange={setInstrument}
              options={[{ value: 'neft', label: 'NEFT' }, { value: 'rtgs', label: 'RTGS' }, { value: 'imps', label: 'IMPS' }, { value: 'upi', label: 'UPI' },
                        { value: 'cheque', label: 'Cheque' }, { value: 'dd', label: 'Demand draft' }, { value: 'cash', label: 'Cash' }]} />
      {cash ? null : (<>
        <Text name="pay-no" label={instrument === 'cheque' || instrument === 'dd' ? 'Cheque / draft no.' : instrument === 'upi' ? 'UPI transaction ref.' : 'UTR no.'}
              value={instrumentNo} onChange={setInstrumentNo} required help="the receipt prints it; the bank statement is matched on it" />
        <Text name="pay-dated" label="Dated" type="date" value={instrumentDate} onChange={setInstrumentDate} help="the date on the instrument, if it differs" />
        <Text name="pay-bank" label="Drawn on" value={drawnOn} onChange={setDrawnOn} help="the bank" />
      </>)}
      <Text name="pay-on" label="Received on" type="date" value={paidOn} onChange={setPaidOn} required />
      <Select name="pay-purpose" label="Towards" value={purpose} onChange={setPurpose}
              options={[{ value: 'against_bills', label: 'Bills outstanding' }, { value: 'security_deposit', label: 'SD — security deposit (held; returned when nothing is owed)' }, { value: 'advance', label: 'Advance against the next bill' }]} />
      <Text name="pay-note" label="Comments" value={note} onChange={setNote} help="printed on the receipt — e.g. SD for 10 nos hydrogen cylinders" />
      <p className="muted">Interest on what stays unpaid is worked out from the terms in force — credit {creditDays} days, grace {graceDays}, {interest}% a month.</p>
      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || paise === null || !paidOn || (!cash && !instrumentNo.trim())}>
        {runner.busy ? 'Recording…' : 'Record the payment'}
      </Button>
    </>
  );
}


/**
 * THE MONTH, PREPARED AND ARGUED WITH BEFORE IT IS SENT (audit, 17 Sep: the
 * calculator and the service existed and no operator could reach them).
 * Prepare draws the draft — re-preparing REPLACES it, which is the late-entry
 * route (D131) — the review shows every line with the days it covers, and
 * Send is the person's own act (D102). A sent month refuses a re-prepare in
 * words; the correction route is an adjustment, never a silent edit.
 */
function PrepareMonth({ customerId, session, onChanged }: { customerId: number; session: Session; onChanged: () => void }) {
  const now = new Date();
  const lastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const sites = useAsk(() => api.view('sites.list', { customerId }), [customerId], MESSAGES);
  const [siteId, setSiteId] = useState('');
  // audit B3 — ONE month picker, not a year box beside a month box. The two
  // number fields let an operator prepare "month 13" or a year he mistyped,
  // and gave him no way to see which month he was actually about to bill.
  // The revenue panel below already uses the browser's own month control; the
  // holding bill now reads the same way.
  const [period, setPeriod] = useState(`${lastMonth.getUTCFullYear()}-${String(lastMonth.getUTCMonth() + 1).padStart(2, '0')}`);
  const year = period.slice(0, 4);
  const month = String(Number(period.slice(5, 7)));
  const [billId, setBillId] = useState<number | null>(null);
  const runner = useRun(MESSAGES);

  const draft = useAsk(() => (billId === null ? Promise.resolve(null) : api.view('bills.get', { billId })), [billId], MESSAGES);

  async function prepare() {
    const ok = await runner.run(async () => {
      const r = await api.command('bill.prepareMonthly', {
        customerId, siteId: Number(siteId), year: Number(year), month: Number(month),
      });
      const v = r.value as { billId: number };
      setBillId(v.billId);
      return r;
    });
    // no manual draft.reload() here: setBillId already triggers the fetch, and
    // a manual reload still sees the OLD billId in this closure — the two race
    // and the stale null answer can land last (found by the browser walk)
    if (ok) onChanged();
  }

  async function send() {
    if (billId === null) return;
    const ok = await runner.run((retryKey) => api.command('bill.send', { billId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { draft.reload(); onChanged(); }
  }

  const d = draft.data;
  return (
    <Card title="Monthly holding bill" sub="prepare the month's holding bill, check every line, then send it">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {can(session, 'bill.prepare') ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Select name="bill-site" label="Site" value={siteId} onChange={setSiteId} placeholder="Choose a site"
                  options={(sites.data?.rows ?? []).map((st) => ({ value: String(st.siteId), label: st.name + (st.isPrimary ? ' (primary)' : '') }))} />
          <Text name="bill-month" label="Month" type="month" value={period} onChange={setPeriod} />
          <Button onClick={() => { void prepare(); }} disabled={runner.busy || !siteId || !/^\d{4}-\d{2}$/.test(period)}>
            {runner.busy ? 'Working…' : billId === null ? 'Prepare the draft' : 'Prepare it again'}
          </Button>
        </div>
      ) : <p className="muted">Preparing needs the bill.prepare function.</p>}

      {d ? (
        <div style={{ marginTop: 14 }}>
          <Banner kind={d.bill.status === 'sent' ? 'good' : 'info'}
                  title={d.bill.status === 'sent'
                    ? `Sent as ${d.bill.number} — a sent month is corrected by adjustment, never re-issued.`
                    : `Draft for ${d.bill.periodStart ?? ''} to ${d.bill.periodEnd ?? ''} — argue with every line before sending.`} />
          <Register
            columns={[
              { key: 'what', head: 'Line', cell: (l: { description: string; coversFrom: string | null; coversTo: string | null }) =>
                  <>{l.description}{l.coversFrom ? <span className="muted"> · {date(l.coversFrom)} – {l.coversTo ? date(l.coversTo) : '…'}</span> : null}</> },
              { key: 'qty', head: 'Qty', right: true, cell: (l: { qty: string | null }) => l.qty ?? '—' },
              { key: 'rate', head: 'Rate', right: true, cell: (l: { ratePaise: number | null }) => l.ratePaise === null ? '—' : <Money paise={l.ratePaise} plain /> },
              { key: 'amount', head: 'Amount', right: true, cell: (l: { amountPaise: number }) => <Money paise={l.amountPaise} plain /> },
              { key: 'gst', head: 'GST', right: true, cell: (l: { gstPaise: number; gstPct: string }) => <><Money paise={l.gstPaise} plain /><span className="muted"> @{l.gstPct}%</span></> },
            ]}
            rows={d.lines.filter((l) => !l.voided)} empty="Nothing to bill for this month at this site." />
          <div className="detail-grid" style={{ marginTop: 10 }}>
            <div><div className="k">Subtotal</div><div className="v"><Money paise={d.bill.subtotalPaise} /></div></div>
            <div><div className="k">GST</div><div className="v"><Money paise={d.bill.gstPaise} /></div></div>
            <div><div className="k">Total</div><div className="v"><Money paise={d.bill.totalPaise} /></div></div>
          </div>
          {d.bill.status !== 'sent' && can(session, 'bill.send') ? (
            <Button kind="primary" onClick={() => { void send(); }} disabled={runner.busy}>
              {runner.busy ? 'Sending…' : 'Send it — this gives it its number and puts it on the account'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}


/**
 * THE AGEING WORKLIST (day-one register): who owes, how old it is, and the
 * bill it sits on — the collector's morning list. Residues derived
 * oldest-first (D67); age runs from the DUE date, not the send date.
 */
function AgingWorklist({ session }: { session: Session }) {
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('account.aging', { page, pageSize: 50 }), [page], MESSAGES);
  if (asked.loading && !asked.data) return <Card title="Who owes, and for how long"><Loading what="the ageing worklist" /></Card>;
  if (asked.error) return <Card title="Who owes, and for how long"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  void session;
  return (
    <Card title="Who owes, and for how long" sub="ageing from the DUE date (credit + grace) — a bill inside its terms is unpaid, not late">
      {/*
        * 0112 — THE BUCKETS ARE THE INVOICES, AND MONEY ON ACCOUNT IS ITS OWN COLUMN.
        * Until this, an unapplied payment was spread over the oldest invoices by
        * assumption, so a customer withholding payment on a disputed invoice had his
        * money silently applied to it and the dispute left the worklist. Now nothing is
        * applied that nobody applied, and the two columns always come back to what he
        * owes — which is why both are shown rather than one.
        */}
      <Banner kind="info" title="Open invoices, and money not yet applied to any of them, are shown apart."
              detail="Open invoices less money on account is what the customer owes, to the rupee. CTS never decides by itself which invoice a payment settled — a person says, on the customer's Billing page." />
      <Register
        columns={[
          { key: 'who', head: 'Customer', cell: (r: { customerName: string }) => r.customerName },
          { key: 'owed', head: 'Owed', right: true, cell: (r: { payablePaise: number }) => <Money paise={r.payablePaise} plain /> },
          { key: 'open', head: 'Open invoices', right: true, cell: (r: { outstandingPaise: number }) => <Money paise={r.outstandingPaise} plain /> },
          { key: 'onacct', head: 'On account', right: true, cell: (r: { onAccountPaise: number }) =>
              r.onAccountPaise ? <Money paise={r.onAccountPaise} plain /> : <span className="muted">—</span> },
          { key: 'current', head: 'Not yet due', right: true, cell: (r: { bucketPaise: { current: number } }) => r.bucketPaise.current ? <Money paise={r.bucketPaise.current} plain /> : <span className="muted">—</span> },
          { key: 'd30', head: '1–30 late', right: true, cell: (r: { bucketPaise: { d30: number } }) => r.bucketPaise.d30 ? <Money paise={r.bucketPaise.d30} plain /> : <span className="muted">—</span> },
          { key: 'd60', head: '31–60', right: true, cell: (r: { bucketPaise: { d60: number } }) => r.bucketPaise.d60 ? <Money paise={r.bucketPaise.d60} plain /> : <span className="muted">—</span> },
          { key: 'd90', head: 'Over 60', right: true, cell: (r: { bucketPaise: { d90plus: number } }) => r.bucketPaise.d90plus ? <Money paise={r.bucketPaise.d90plus} plain /> : <span className="muted">—</span> },
          { key: 'oldest', head: 'Oldest unpaid', cell: (r: { oldestNumber: string | null; oldestDueOn: string | null; oldestOverdueDays: number; estimatedOnCurrentTerms: number }) =>
              r.oldestNumber
                ? <>
                    {r.oldestNumber}
                    <span className="muted"> · due {r.oldestDueOn ? date(r.oldestDueOn) : '—'}{r.oldestOverdueDays > 0 ? ` · ${r.oldestOverdueDays} days late` : ''}</span>
                    {/* D158 — SAID OUT LOUD WHEN THE DATE IS AN ESTIMATE.
                      * A bill sent before `terms_snapshot` existed has no terms of its own, so
                      * its due date is worked out on the customer's CURRENT terms — which means
                      * it MOVES if somebody renegotiates. The figure was being presented as
                      * definitive; review found that nothing said so. Legacy snapshots are not
                      * silently backfilled with guesses: the screen admits the estimate. */}
                    {r.estimatedOnCurrentTerms > 0
                      ? <div><Pill tone="muted">{`${r.estimatedOnCurrentTerms} estimated on today's terms`}</Pill></div>
                      : null}
                  </>
                : <span className="muted">—</span> },
          // A row that does not reconcile is a FINDING, not a rounding difference, and
          // the screen says so rather than showing three numbers that do not agree.
          { key: 'agrees', head: '', cell: (r: { reconciles: boolean }) =>
              r.reconciles ? null : <Pill tone="danger">does not reconcile</Pill> },
        ]}
        rows={d.rows} empty="Nobody owes anything. A good morning."
        onOpen={(r) => go(`#/customers/${r.customerId}?tab=bills`)} asOf={d.asOf} />
      <Pager page={d.page} onPage={setPage} />
    </Card>
  );
}

/**
 * A MONTH'S REVENUE, BY WHAT EARNED IT (db/queries/04_revenue_by_kind.sql,
 * exposed 22 Sep). Read from the charges, not the bill totals, so a correction
 * counts against the month it happened in (D160) rather than the month billed.
 */
function RevenueByKind() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const asked = useAsk(() => api.view('bills.revenue', { month }), [month], MESSAGES);
  return (
    <Card title="What the month earned, by what earned it" sub="from the charges, not the bill totals — a correction counts against the month it happened in">
      <div className="toolbar">
        <Text name="revenue-month" label="Month" type="month" value={month} onChange={setMonth} />
      </div>
      {asked.loading && !asked.data ? <Loading what="the month's revenue" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : asked.data!.lines.length === 0 ? <EmptyState title="Nothing earned this month." detail="No charge has this month's effective date yet." />
        : (
          <Register
            columns={[
              { key: 'kind', head: 'Earned on', cell: (r: { earnedOn: string }) => r.earnedOn },
              { key: 'lines', head: 'Lines', right: true, cell: (r: { lines: number }) => count(r.lines) },
              { key: 'net', head: 'Net', right: true, cell: (r: { netPaise: number }) => <Money paise={r.netPaise} plain /> },
              { key: 'tax', head: 'Tax', right: true, cell: (r: { taxPaise: number }) => <Money paise={r.taxPaise} plain /> },
              { key: 'total', head: 'Total', right: true, cell: (r: { totalPaise: number }) => <Money paise={r.totalPaise} plain /> },
              { key: 'unbilled', head: 'Not yet billed', right: true, cell: (r: { notYetBilledPaise: number }) => r.notYetBilledPaise ? <Money paise={r.notYetBilledPaise} plain /> : <span className="muted">—</span> },
            ]}
            rows={asked.data!.lines} empty="Nothing earned this month."
            totals={<span>Total earned in {month}: <b><Money paise={asked.data!.totalPaise} plain /></b></span>} />
        )}
    </Card>
  );
}
